import { NotificationType } from '@kcftech/prometheustypescriptapiclient';
import { CreateButton } from 'components/button/CreateButton';
import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { TablePageLayout } from 'components/page/styled';
import {
    GroupedNotificationSubscriptionsFilter,
    usePagedGroupedNotificationSubscriptionsDataSource,
} from 'custom_hooks/data_hooks/data_source/groupedNotificationSubscriptionsDataSource';
import { CreateNotificationSubscriptionModal } from 'pages/Settings/NotificationSubscriptions/CreateNotificationSubscriptionModal';
import { GroupedNotificationSubscriptionsTable } from 'pages/Settings/NotificationSubscriptions/GroupedNotificationSubscriptionsTable';
import { NotificationSubscriptionsFilterPane } from 'pages/Settings/NotificationSubscriptions/NotificationSubscriptionsFilterPane';
import {} from 'pages/Settings/NotificationSubscriptions/shared';
import { SearchCreateContainer } from 'pages/Settings/NotificationSubscriptions/styled';
import { useEffect, useMemo, useState } from 'react';
import { queryClient } from 'utils/query';
import { ContextualPageProps } from 'utils/types';

export const GroupViewTableQueryKey = 'grouped-subscriptions-table';

export function GroupViewTab({ corporation, location }: ContextualPageProps) {
    const [nameSearch, setNameSearch] = useState('');
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [typeFilters, setTypeFilters] = useState<NotificationType[]>([]);

    const defaultFilter = useMemo<GroupedNotificationSubscriptionsFilter>(
        () => ({
            corporationId: corporation?.Id,
            locationId: location?.Id,
            nameSearch,
        }),
        [corporation?.Id, location?.Id, nameSearch]
    );

    const dataSource = usePagedGroupedNotificationSubscriptionsDataSource({
        name: GroupViewTableQueryKey,
        defaultFilter,
    });

    const { setFilter } = dataSource;
    useEffect(() => {
        setFilter((current) => ({
            ...current,
            nameSearch,
            notificationTypes: typeFilters,
            locationId: location?.Id,
            corporationId: corporation?.Id,
        }));
    }, [setFilter, nameSearch, typeFilters, location?.Id, corporation?.Id]);

    return (
        <TablePageLayout>
            <div className="flex-row flex-wrap mb-20">
                <SearchCreateContainer>
                    <div className="flex-row align-center">
                        <div className="pr-10">
                            <NotificationSubscriptionsFilterPane
                                filters={typeFilters}
                                onChange={setTypeFilters}
                            />
                        </div>
                        <DebouncedSearchInput
                            placeholder="Search"
                            value={nameSearch}
                            onChange={(text) => setNameSearch(text)}
                        />
                    </div>
                    <CreateButton
                        title="Create Notification Subscription"
                        ariaLabel="Create Notification Subscription"
                        onClick={() => setIsCreateModalVisible(true)}
                    />
                </SearchCreateContainer>
            </div>
            <GroupedNotificationSubscriptionsTable
                dataSource={dataSource}
                location={location}
                corporation={corporation}
            />
            {isCreateModalVisible && (
                <CreateNotificationSubscriptionModal
                    location={location}
                    corporation={corporation}
                    handleClose={() => setIsCreateModalVisible(false)}
                    onCreate={() => {
                        queryClient.invalidateQueries({
                            queryKey: [GroupViewTableQueryKey],
                        });
                    }}
                    defaultNotificationType={NotificationType.Indicator}
                />
            )}
        </TablePageLayout>
    );
}
